<template>
  <section class="edit-profile">
    <PageHeader showBreadcrumbs :customBreadcrumbs="customBreadcrumbs" page-title="Edit profile" wrapperClassName="title-container__my-profile">
      <template #buttons>
        <CloseButton :onClick="onClose" />
      </template>
    </PageHeader>
    <div class="block-wrapper">

      <div class="row">
        <div class="col-4">
          <div class="mb-3 text-center" style="padding-top: 10px; padding-right: 60px; padding-left: 35px;">
            <UserAvatar
              v-model="editProfile.avatarFile"
              :userAvatar="userAvatar"
              @resetAvatarInputAfterModalClose="resetAvatarInputAfterModalClose"
              @uploadCroppedAvatar="uploadCroppedAvatar"
              @onAvatarFile="onAvatarFile"
            />
            <p class="name">{{ userName }}</p>
            <p class="position">{{ userPosition }}</p>
            <InfoBlockWrapper v-if='isUserProfile' class="mt-2 text-left">
              <span>User profile is publicly available within your company’s available profiles at the linkbelow:</span>
              <br />
              <a target="_blank" href="/available-profiles">{{ url }}/available-profiles</a>.
              <br />
              <span>To ensure a complete profile, all fields must be filled out except for the CV link.</span>
            </InfoBlockWrapper>
          </div>
        </div>
        <div class="col-8">
          <div class="row">
            <template v-if="!isUserProfile">
              <div class="col-12">
                <PageHeader page-title="User details" wrapperClassName="" titleClassName="block-title" />
                <FormGenerator class="mt-4" :data="userDetails.form" :showFormControls="false"
                  @submit="submitUserDetails" ref="detailsForm" />
              </div>
              <div class="col-12">
                <div class="break-line" />
              </div>
            </template>
            <div class="col-12">
              <PageHeader page-title="Profile details" wrapperClassName="" titleClassName="block-title" />
              <FormGenerator :data="editProfile.form" :showFormControls="false" @input="inputFormField"
                @submit="submitEditData" ref="form" />
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <PageHeader page-title="Contact details" titleClassName="block-title" wrapperClassName="mb-0">
                <template #buttons>
                  <b-button class="button justify-content-center chevron-button" style="height: 32px;" variant="plain"
                    @click="toggleShowingContactDetails">
                    <span class="material-symbols-outlined material-symbols-fill-1 mr-1 ml-2"
                      :style="{ transform: !isShowContactDetails ? 'rotate(180deg)' : 'none', transition: '0.2s' }">
                      keyboard_arrow_down
                    </span>
                  </b-button>
                </template>
              </PageHeader>
              <b-collapse v-model="isShowContactDetails">
                <FormGenerator class="mt-4" :data="userContactDetails.form" :showFormControls="false"
                  @submit="submitUserContactDetails" ref="contactDetailsForm" />
              </b-collapse>
            </div>
          </div>
        </div>
        <div class="col-4">
        </div>
      </div>
    </div>
    <StickyButtonsWrapper :isShowAnimation="isShowStickyButtonsAnimation" :loading="isLoading || isSliderDataLoading" v-if="isShowStickyButtons">
      <b-button variant="outline"
        class="button title-container__controls-item justify-content-center min-width-130 max-height-32"
        @click="onClose">
        Cancel
      </b-button>
      <b-button variant="primary" class="button justify-content-center min-width-190" @click.prevent="submit">
        Save changes
      </b-button>
    </StickyButtonsWrapper>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FormGenerator from '@components/FormGenerator'
import UserMixin from '@mixins/UserMixin'
import CloseButton from '@components/common/CloseButton.vue'
import PageHeader from '@components/PageHeader.vue'
import UserAvatar from '@components/UserAvatar.vue'
import StickyButtonsWrapper from '@components/forms/StickyButtonsWrapper'
import InfoBlockWrapper from '@/components/common/InfoBlockWrapper'
import UserContactDetailsMixin from '@mixins/UserContactDetailsMixin'
import UserDetailsMixin from '@mixins/UserDetailsMixin'

export default {
  name: 'EditProfile',
  mixins: [UserDetailsMixin, UserMixin, UserContactDetailsMixin],
  components: {
    FormGenerator,
    CloseButton,
    PageHeader,
    UserAvatar,
    StickyButtonsWrapper,
    InfoBlockWrapper
  },
  emits: ['close', 'profileUpdate', 'avatarUpdate', 'getName', 'loadingChange'],
  props: {
    userId: { type: [Number, String, null], default: null },
    userData: { type: Object, default: null },
    isUserProfile: { type: Boolean, default: false },
    isMyProfile: { type: Boolean, default: false },
    isShowStickyButtonsAnimation: { type: Boolean, default: false },
    breadcrumbs: { type: Array, default: () => [] },
    isSliderDataLoading: { type: Boolean, default: false }
  },
  computed: {
    ...mapGetters({
      isAdmin: 'isAdmin',
      getCurrentUserId: 'getCurrentUserId',
      getLoadedUser: 'users/getLoadedUser'
    }),
    customBreadcrumbs () {
      if (this.breadcrumbs.length > 0) {
        return this.breadcrumbs
      }

      if (this.isMyProfile) {
        return [
          {
            text: 'Home',
            to: { name: 'home' }
          },
          {
            text: 'My profile',
            to: { name: 'my-profile' }
          }
        ]
      } else {
        if (!this.userName) {
          return []
        }
        return [
          {
            text: 'Home',
            to: { name: 'home' }
          },
          {
            text: this.userName,
            to: { name: 'profile' }
          }
        ]
      }
    },
    isShowOnWidget () {
      try {
        return !!(this.table && this.table.items && this.table.items[0] && this.table.items[0].show_on_widget)
      } catch (error) {
        return false
      }
    },
    isInvitationAccepted () {
      try {
        return this.table && this.table.items && this.table.items[0] && this.table.items[0].status === 'active'
      } catch (error) {
        // Error accessing user status
        return false
      }
    }
  },
  data () {
    return {
      userName: '',
      userPosition: '',
      userAvatar: '',
      mainProps: { width: 75, height: 75, class: 'm1', src: '/images/avatar-placeholder.png' },
      readonly: true,
      table: {
        items: []
      },
      isAvatarUploading: false,
      url: window.location.hostname,
      isShowContactDetails: true,
      userContactDetailsData: null,
      userDetailsData: null,
      editData: null,
      isShowStickyButtons: true,
      isDetailsFormSubmitted: false,
      isEditFormSubmitted: false,
      isContactDetailsFormSubmitted: false,
      isLoading: false
    }
  },
  created () {
    this.disableUserDetailsEmail()

    if (!this.isUserProfile) {
      this.hideContactDetailsEmailField()
    }
  },
  async mounted () {
    this.isLoading = true
    this.$emit('loadingChange', true)

    await this.fetchUserData()

    this.isLoading = false
    this.$emit('loadingChange', false)
  },
  methods: {
    ...mapActions({
      setLoadedUser: 'users/setLoadedUser'
    }),
    async fetchUserData () {
      // Always fetch fresh data to ensure we have the latest show_on_widget value and other details

      try {
        const response = this.isUserProfile ? await this.fetchUserProfile(this.userId) : await this.fetchUser(this.userId)
        const data = response.data

        this.setLoadedUser(data)

        this.readonly = false
        this.userName = data.name
        this.userPosition = data.position
        this.userAvatar = data.avatar_url

        this.updateAllForms(data)

        this.updateValidations()
        if (this.isAdmin) {
          if (this.userId === this.getCurrentUserId) {
            const updatedOptions = this.userDetails.form.roles.options.map(role => role.name === 'administrator' ? { ...role, $isDisabled: true } : role)
            this.setUserDetailsRolesOptions(updatedOptions)
          }
        } else {
          this.disableUserDetailsRoles()
        }
      } catch (err) {
        console.error(err)
      }
    },
    onAvatarFile (e) {
      this.handleAvatarFile(e, 'editProfile')
    },
    updateAllForms (data) {
      let contactData = data.contacts
      if (data.email) {
        contactData = {
          ...contactData,
          email: data.email
        }
      }
      this.setTable(data)
      this.fillContactDetailsData(contactData)
      this.fillEditUserForm(data)
      this.fillUserDetailsData(data)
    },
    submitUserDetails (data) {
      this.isDetailsFormSubmitted = true
      this.userDetailsData = data
    },
    submitEditData (values) {
      this.isEditFormSubmitted = true
      this.editData = values
    },
    submitUserContactDetails (data) {
      if (!this.isUserProfile) {
        delete data.email
      }
      this.isContactDetailsFormSubmitted = true
      this.userContactDetailsData = data
    },
    toggleShowingContactDetails () {
      this.isShowContactDetails = !this.isShowContactDetails
    },
    onClose () {
      if (this.isShowStickyButtonsAnimation) {
        this.isShowStickyButtons = false
        setTimeout(() => {
          this.$emit('close')
        })
        return
      }

      this.$emit('close')
    },
    inputFormField (fields) {
      if (fields.employment_type.value?.id === 'partner') {
        fields.partner.hidden = false
      } else {
        fields.partner.hidden = true
      }
    },
    async submit () {
      this.isDetailsFormSubmitted = false
      this.isEditFormSubmitted = false
      this.isContactDetailsFormSubmitted = false

      // Submit all forms
      if (!this.isUserProfile) {
        this.$refs.detailsForm.submit()
      }

      this.$refs.form.submit()
      this.$refs.contactDetailsForm.submit()

      // Wait a brief moment for the form submission handlers to execute
      await this.$nextTick()

      // Small delay to ensure all form handlers have completed
      await new Promise(resolve => setTimeout(resolve, 100))

      const validationArray = []

      if (!this.isUserProfile) {
        validationArray.push(this.isDetailsFormSubmitted)
      }

      validationArray.push(this.isEditFormSubmitted)
      validationArray.push(this.isContactDetailsFormSubmitted)

      if (validationArray.some(el => el === false)) {
        // Form validation failed - some forms did not submit properly
        return
      }

      let data = {
        ...this.userContactDetailsData,
        ...this.editData,
        ...this.userDetailsData
      }

      this.isLoading = true
      this.$emit('loadingChange', true)
      try {
        const response = this.isUserProfile ? await this.updateProfile(data) : await this.updateUser(data)
        if (response.status === 200) {
          this.$snotify.success('Profile successfully updated', 'Success', this.$store.state.snotify)
        }
        data = response.data
        this.userName = data.name
        // this.userAvatar = data.avatar_url

        this.userPosition = data.position

        this.updateAllForms(data)

        this.$emit('profileUpdate', data)
      } catch (err) {
        this.errorNotifications(err)
      } finally {
        this.isLoading = false
        this.$emit('loadingChange', false)
      }
    },
    setTable (res) {
      const data = { ...res }
      data.userStatus = data.deleted_at === null && data.exit_at === null && data.status === 'approved'
      data.no_reporting = !!data.no_reporting
      data.employment_type_full_label = data?.employment_type?.label
        ? data.employment_type.label + (data.partner && data.employment_type.type === 'partner' ? ` (${data.partner.company_name})` : '')
        : data?.employment_type?.name
      data.availability = data.availability && data.availability.name ? data.availability.name : ''
      data.seniority_lvl = data.seniority_lvl && data.seniority_lvl.name ? data.seniority_lvl.name : ''
      data.english_lvl = data.english_lvl && data.english_lvl.name ? data.english_lvl.name : ''
      this.table.items = [data]
    },
    base64ToFile (base64String, fileName, mimeType) {
      const byteCharacters = atob(base64String.split(',')[1])
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: mimeType })

      return new File([blob], fileName, { type: mimeType })
    },

    async uploadCroppedAvatar (data) {
      this.userAvatar = await this.fileToBase64(data)
      const avatar = new File([data], 'image', { type: 'image/jpeg' })
      if (avatar) {
        this.editProfile.avatarFile = avatar
        const form = new FormData()
        form.append('file', avatar)
        const requestUrl = this.isUserProfile ? `/api/users/profiles/${this.userId}/update-avatar` : `/api/users/${this.userId}/update-avatar`
        this.isAvatarUploading = true
        this.axios.post(
          requestUrl,
          form,
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          }).then(response => {
          if (response.status === 200) {
            this.$snotify.success('Profile successfully updated', 'Success', this.$store.state.snotify)
            this.$emit('avatarUpdate', response.data.avatar_url)
          }
        }).catch(err => {
          this.errorNotifications(err)
        }).finally(() => {
          this.isAvatarUploading = false
          this.editProfile.showAvatarCropModal = false
        })
      }
    },
    updateValidations () {
      let requiredUserFields = [
        'employment_type',
        'allocations',
        'payment_type',
        'roles',
        'partner'
      ]

      if (this.isShowOnWidget) {
        requiredUserFields = requiredUserFields.concat(['years_of_experience', 'availability', 'seniority_lvl', 'english_lvl', 'position'])
      }

      if (this.isInvitationAccepted) {
        requiredUserFields.push('name')
      }

      const requiredUserProfileFields = [
        'name',
        'employment_type',
        'allocations',
        'payment_type',
        'years_of_experience',
        'availability',
        'seniority_lvl',
        'english_lvl',
        'position',
        'partner'
      ]

      const requiredFields = this.isUserProfile ? requiredUserProfileFields : requiredUserFields

      if (this.isUserProfile) {
        this.updateValidationRules([{ key: 'email', validation: [{ name: 'required' }, { name: 'email' }] }])
      }

      requiredFields.forEach(field => {
        if (field === 'partner') {
          this.editProfile.form[field].validation = [{ name: 'requiredIf', params: () => this.editProfile.form.employment_type.value.id === 'partner' }]
        } else if (field === 'roles') {
          this.userDetails.form[field].validation = [{ name: 'required' }]
        } else {
          this.editProfile.form[field].validation = [{ name: 'required' }]
        }
        if (this.editProfile.form[field]) {
          this.editProfile.form[field].label += ' *'
        }
      })
    }
  },
  watch: {
    userName (nameValue) {
      if (this.$emit('getName')) {
        this.$emit('getName', nameValue)
      }
    }
  }
}
</script>
